import React from "react";

import NavBar from "../components/common/nav_bar";
import MaintenanceBanner from "../components/common/maintenance_banner";
import ScrollToTopButton from "../components/common/scroll_to_top_button";
import Skeleton from "../components/common/skeleton";
import StaleBanner from "../components/paper/StaleBanner";

import { fetchPaperHealth } from "../services/paperApi";
import { requireLoginOrThrow } from "../utils/auth";
import { getNavCollapsed, setNavCollapsed } from "../utils/navState";
import { formatKst } from "../utils/time";

const POLL_INTERVAL_MS = 5_000;
const STALE_FAIL_THRESHOLD = 3;

/**
 * PaperHealthPage — /research/health.
 *
 * paper-trader /api/health 응답을 5 초 주기로 폴링. uptime / RSS /
 * Goroutines / 데이터 수집 stale (mark_stale_sec / last_trade_ts) 표시.
 * BCR-109 에서 추가한 last_mark_ts + mark_stale_sec 가 핵심 — collector
 * liveness 와 trade idle 을 분리해서 진단.
 */
class PaperHealthPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapsed: getNavCollapsed(),
            loading: true,
            health: null,
            failureCount: 0,
            stale: false,
            lastUpdated: null,
        };
        this.shouldPoll = true;
        this.isPolling = false;
    }

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        document.addEventListener("visibilitychange", this.onVisibilityChange);
        this.pollData();
    }

    componentWillUnmount() {
        this.shouldPoll = false;
        clearTimeout(this.pollTimer);
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }

    onVisibilityChange = () => {
        this.shouldPoll = !document.hidden;
        if (this.shouldPoll && !this.isPolling) this.pollData();
    };

    pollData = () => {
        if (!this.shouldPoll) return;
        this.isPolling = true;
        fetchPaperHealth()
            .then((res) => {
                if (!this.shouldPoll) return;
                this.setState({
                    health: res.data,
                    failureCount: 0,
                    stale: false,
                    lastUpdated: new Date(),
                    loading: false,
                });
            })
            .catch(() => {
                if (!this.shouldPoll) return;
                const failureCount = this.state.failureCount + 1;
                this.setState({
                    failureCount,
                    stale: failureCount >= STALE_FAIL_THRESHOLD,
                    loading: false,
                });
            })
            .finally(() => {
                this.isPolling = false;
                if (this.shouldPoll)
                    this.pollTimer = setTimeout(this.pollData, POLL_INTERVAL_MS);
            });
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    render() {
        const { collapsed, loading, health, stale, lastUpdated } = this.state;

        const markStale = health?.mark_stale_sec ?? 0;
        const markStaleTone =
            markStale > 60 ? "loss" : markStale > 15 ? "warning" : "gain";

        return (
            <div className="min-h-screen w-full overflow-x-hidden bg-surface text-content sm:overflow-x-auto">
                <NavBar
                    activeMenu="research"
                    collapsed={collapsed}
                    onCollapse={this.onCollapse}
                />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed
                            ? "sm:pl-[88px] lg:pl-[96px]"
                            : "sm:pl-[252px] lg:pl-[260px]"
                    } px-4 py-4 sm:pr-8 sm:py-6 lg:pr-10`}
                    style={{ marginTop: "var(--maintenance-banner-h, 0px)" }}
                >
                    <MaintenanceBanner />
                    <header className="mb-6">
                        <h1 className="text-2xl font-bold text-content">
                            paper-trader Health
                        </h1>
                        <p className="text-sm text-content-secondary mt-1">
                            Mac 로컬 paper-trader (BCR-109) 의 프로세스/데이터 흐름 상태.
                        </p>
                    </header>

                    <StaleBanner
                        stale={stale}
                        lastUpdated={lastUpdated}
                        onRetry={this.pollData}
                    />

                    {loading && !health ? (
                        <>
                            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                                {[0, 1, 2, 3].map((i) => (
                                    <Skeleton key={i} variant="card" height="72px" />
                                ))}
                            </section>
                            <Skeleton variant="card" height="220px" />
                        </>
                    ) : (
                        <>
                            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 sticky top-12 z-20 bg-surface -mx-4 px-4 py-2 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:bg-transparent">
                                <KpiCard
                                    label="Uptime"
                                    value={fmtUptime(health?.uptime_sec)}
                                />
                                <KpiCard
                                    label="RSS"
                                    value={
                                        health?.rss_mb != null
                                            ? `${health.rss_mb.toFixed(1)} MB`
                                            : "—"
                                    }
                                />
                                <KpiCard
                                    label="Goroutines"
                                    value={String(health?.goroutines ?? "—")}
                                />
                                <KpiCard
                                    label="Mark Stale"
                                    value={`${markStale}s`}
                                    tone={markStaleTone}
                                />
                            </section>

                            <section className="bg-surface-card border border-surface-border rounded-lg p-4 mb-6">
                                <h2 className="text-base font-semibold text-content mb-3">
                                    데이터 흐름
                                </h2>
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                                    <Field
                                        label="마지막 mark price 수신"
                                        value={fmtTs(health?.last_mark_ts)}
                                    />
                                    <Field
                                        label="마지막 trade 발생"
                                        value={fmtTs(health?.last_trade_ts)}
                                    />
                                    <Field
                                        label="trade idle (min)"
                                        value={
                                            health?.stale_min != null
                                                ? `${health.stale_min}`
                                                : "—"
                                        }
                                    />
                                    <Field
                                        label="mark symbols"
                                        value={String(health?.mark_symbols ?? "—")}
                                    />
                                    <Field
                                        label="시작 시각"
                                        value={fmtTs(health?.started_at)}
                                    />
                                    <Field
                                        label="Go version"
                                        value={health?.go_version ?? "—"}
                                    />
                                </dl>
                            </section>

                            {health?.bus_drops && Object.keys(health.bus_drops).length > 0 && (
                                <details className="bg-surface-card border border-surface-border rounded-lg p-4 mb-6">
                                    <summary className="text-base font-semibold text-content cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand rounded">
                                        Bus drops (subscriber overflow)
                                    </summary>
                                    <pre className="text-xs text-content-secondary overflow-x-auto mt-3">
                                        {JSON.stringify(health.bus_drops, null, 2)}
                                    </pre>
                                </details>
                            )}

                            <details className="bg-surface-card border border-surface-border rounded-lg p-4">
                                <summary className="text-base font-semibold text-content cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand rounded">
                                    raw /api/health (디버그)
                                </summary>
                                <pre className="text-xs text-content-secondary overflow-x-auto mt-3 max-h-96 overflow-y-auto">
                                    {JSON.stringify(health, null, 2)}
                                </pre>
                            </details>
                        </>
                    )}

                    <ScrollToTopButton />
                </main>
            </div>
        );
    }
}

function KpiCard({ label, value, tone = "neutral" }) {
    const toneClass =
        tone === "gain"
            ? "text-gain"
            : tone === "loss"
              ? "text-loss"
              : tone === "warning"
                ? "text-warning"
                : "text-content";
    return (
        <div className="bg-surface-card border border-surface-border rounded-lg p-3">
            <div className="text-xs text-content-muted">{label}</div>
            <div className={`text-base sm:text-lg font-semibold mt-1 ${toneClass}`}>
                {value}
            </div>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div className="flex justify-between border-b border-surface-border-light py-1">
            <dt className="text-content-muted">{label}</dt>
            <dd className="text-content">{value}</dd>
        </div>
    );
}

function fmtTs(ts) {
    if (!ts) return "—";
    return formatKst(ts);
}

function fmtUptime(sec) {
    if (sec == null) return "—";
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${h}h ${m}m`;
}

export default PaperHealthPage;
